/**
 * Seed ↔ URL hash binding.
 *
 * The hash is the single source of truth: everything that changes the seed goes
 * through `location.hash`, and `hashchange` is the ONE regeneration entry point.
 * That makes back/forward navigation work for free.
 */

const SEED_LEN = 10;

export function mintSeed(): string {
  const bytes = new Uint32Array(SEED_LEN);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < SEED_LEN; i++) {
    out += (bytes[i] % 36).toString(36);
  }
  return out;
}

function readHash(): string {
  try {
    return decodeURIComponent(location.hash.slice(1));
  } catch {
    return location.hash.slice(1);
  }
}

/**
 * Wire the hash to `onSeed`. Fires once immediately (minting + `replaceState`
 * if the hash is empty, so the first load doesn't leave a blank history entry),
 * then once per genuine hash change.
 */
export function initSeed(onSeed: (seed: string) => void): void {
  let current: string | null = null;

  const emit = (seed: string) => {
    if (seed === current) return; // echo guard
    current = seed;
    // The title carries the seed so history entries read as tracks.
    document.title = `marbles - #${seed}`;
    onSeed(seed);
  };

  addEventListener("hashchange", () => {
    const seed = readHash();
    if (seed === "") {
      // Someone cleared the hash — mint rather than regenerate nothing.
      newSeed();
      return;
    }
    emit(seed);
  });

  const initial = readHash();
  if (initial === "") {
    const seed = mintSeed();
    history.replaceState(null, "", "#" + seed);
    emit(seed);
  } else {
    emit(initial);
  }
}

/** Mint a fresh seed and push it — the hashchange does the regeneration. */
export function newSeed(): string {
  const seed = mintSeed();
  location.hash = seed;
  return seed;
}
